// =============================================================================
// components/ui/CryptoMesh.tsx — the "decentralized network" design motif.
//
// A subtle mesh of connected NODES (dots + thin edges) that reads as a
// blockchain / peer-to-peer network — the visual signifier CRYB was missing to
// feel crypto-native + decentralized. Layers UNDER content (pointerEvents none),
// pairs with the glass system and the aurora ParticleBackground.
//
// Deliberately STATIC (no reanimated worklets — after the live-room mount-crash
// lessons, decorative chrome must never run a worklet on mount) + deterministic
// (seeded layout, stable across renders, no flicker). Pure react-native-svg,
// already a project dep. Two theme modes (light default / dark) referencing the
// brand palette. Keep it quiet — it's texture, not the subject.
// =============================================================================
import React, { useMemo } from 'react';
import { StyleSheet, useWindowDimensions, ViewStyle } from 'react-native';
import Svg, { Line, Circle } from 'react-native-svg';
import { ThemeMode } from '../../constants/theme';

export interface CryptoMeshProps {
  /** 'light' (default) or 'dark'. */
  theme?: ThemeMode;
  /** Node count. Keep modest for perf. Default 14. */
  count?: number;
  /** Overall opacity of the whole mesh layer. Default 1. */
  intensity?: number;
  style?: ViewStyle;
}

// Deterministic pseudo-random so the mesh is stable across renders.
function seeded(i: number, salt: number) {
  const x = Math.sin((i + 1) * 12.9898 + salt * 78.233) * 43758.5453;
  return x - Math.floor(x);
}

const TINT = {
  light: { node: '#6C5CE7', edge: '#7C4DFF', nodeOpacity: 0.22, edgeOpacity: 0.10 },
  dark: { node: '#A855F7', edge: '#7C4DFF', nodeOpacity: 0.34, edgeOpacity: 0.16 },
} as const;

/**
 * A subtle decentralized node-mesh. Drop it in as an absolute-fill background
 * layer (like ParticleBackground): <CryptoMesh style={StyleSheet.absoluteFill} />.
 */
export default function CryptoMesh({ theme = 'light', count = 14, intensity = 1, style }: CryptoMeshProps) {
  const { width, height } = useWindowDimensions();
  const t = theme === 'dark' ? TINT.dark : TINT.light;

  const { nodes, edges } = useMemo(() => {
    const W = width || 390;
    const H = height || 800;
    // Place nodes on a jittered loose grid so they spread evenly (not clumped).
    const cols = Math.max(3, Math.round(Math.sqrt(count * (W / H)) * 1.1));
    const rows = Math.max(3, Math.ceil(count / cols));
    const pts: { x: number; y: number; r: number }[] = [];
    let idx = 0;
    for (let r = 0; r < rows && pts.length < count; r++) {
      for (let c = 0; c < cols && pts.length < count; c++) {
        const jx = (seeded(idx, 1) - 0.5) * (W / cols) * 0.7;
        const jy = (seeded(idx, 2) - 0.5) * (H / rows) * 0.7;
        pts.push({
          x: ((c + 0.5) / cols) * W + jx,
          y: ((r + 0.5) / rows) * H + jy,
          r: 1.4 + seeded(idx, 3) * 1.8,
        });
        idx++;
      }
    }
    // Connect each node to its 1–2 nearest neighbours → a sparse mesh (dedup pairs).
    const seen = new Set<string>();
    const es: { x1: number; y1: number; x2: number; y2: number }[] = [];
    for (let i = 0; i < pts.length; i++) {
      const dists = pts
        .map((p, j) => ({ j, d: (p.x - pts[i].x) ** 2 + (p.y - pts[i].y) ** 2 }))
        .filter((o) => o.j !== i)
        .sort((a, b) => a.d - b.d);
      const links = 1 + Math.round(seeded(i, 4)); // 1 or 2
      for (let k = 0; k < links && k < dists.length; k++) {
        const j = dists[k].j;
        const key = i < j ? `${i}-${j}` : `${j}-${i}`;
        if (seen.has(key)) continue;
        seen.add(key);
        es.push({ x1: pts[i].x, y1: pts[i].y, x2: pts[j].x, y2: pts[j].y });
      }
    }
    return { nodes: pts, edges: es };
  }, [width, height, count]);

  return (
    <Svg
      pointerEvents="none"
      style={[StyleSheet.absoluteFill, { opacity: intensity }, style]}
      width={width}
      height={height}
    >
      {edges.map((e, i) => (
        <Line
          key={`e${i}`}
          x1={e.x1}
          y1={e.y1}
          x2={e.x2}
          y2={e.y2}
          stroke={t.edge}
          strokeWidth={0.75}
          strokeOpacity={t.edgeOpacity}
        />
      ))}
      {nodes.map((n, i) => (
        <Circle key={`n${i}`} cx={n.x} cy={n.y} r={n.r} fill={t.node} fillOpacity={t.nodeOpacity} />
      ))}
    </Svg>
  );
}
